import AsyncRequestError from '../../errors/asyncRequestError';
import TimeOutError from '../../errors/timeoutError';
import valueUtils from '../../utils/valueUtils';
import log from '../logger/log';

const STATUS_CHECK_TIMEOUT = 15 * 1000; // 15 seconds

const createAsyncRequestService = ({prefix, successStatus, failedStatus, pendingStatus, checkRequestStatus}) => {
    const asyncRequestMap = new Map();

    class AsyncRequest {
        constructor(dusn, requestId, timeout, statusCheckTimeout, resolve, reject) {
            this.dusn = dusn;
            this.requestId = requestId;
            this.timeout = timeout;
            this.statusCheckTimeout = statusCheckTimeout;
            this.lastResponseTime = Date.now();
            this.resolve = resolve;
            this.reject = reject;
            this.id = null;
            this.statusCheckId = null;
            this.isMount = true;

            this.startTimeout();
            this.startStatusCheck();
        }

        clear() {
            this.isMount = false;
            if (this.id) {
                clearTimeout(this.id);
                this.id = null;
            }
            if (this.statusCheckId) {
                clearTimeout(this.statusCheckId);
                this.statusCheckId = null;
            }
        }

        startTimeout() {
            if (this.timeout > 0 && this.isMount) {
                const timePassed = Date.now() - this.lastResponseTime;
                const adjustedTimeout = Math.max(this.timeout - timePassed, 0);

                if (this.id) clearTimeout(this.id);

                this.id = setTimeout(() => {
                    this.reject(new TimeOutError(`Async request [${this.requestId}] execution timed out.`));
                    removeRequest(this.requestId);
                    log.debug(`${prefix} TIMEOUT REQUEST: RequestId ${this.requestId}`, this.dusn);
                    this.clear();
                }, adjustedTimeout);
            }
        }

        startStatusCheck() {
            if (this.statusCheckTimeout > 0 && this.isMount) {
                if (this.statusCheckId) clearTimeout(this.statusCheckId);

                this.statusCheckId = setTimeout(async () => {
                    try {
                        await checkRequestStatus(this.dusn, this.requestId);
                    } catch (e) {
                        log.error(
                            `${prefix} Error checking status for requestId ${this.requestId}: ${e.message}`,
                            e,
                            this.dusn
                        );
                    }
                    this.startStatusCheck();
                }, this.statusCheckTimeout);
            }
        }

        updateLastResponseTime() {
            this.lastResponseTime = Date.now();
            this.startTimeout();
            this.startStatusCheck();
        }
    }

    const removeRequest = (requestId) => {
        asyncRequestMap.delete(requestId);
    };

    const waitAsyncRequestComplete = (dusn, requestId, timeout, statusCheckTimeout = STATUS_CHECK_TIMEOUT) => {
        const existingRequest = asyncRequestMap.get(requestId);

        if (existingRequest) {
            // return the same promise to avoid duplicate requests / races
            return existingRequest.promise;
        }

        let asyncRequest;

        const promise = new Promise((resolve, reject) => {
            asyncRequest = new AsyncRequest(dusn, requestId, timeout, statusCheckTimeout, resolve, reject);
        });

        // attach promise so future callers can reuse it
        asyncRequest.promise = promise;

        asyncRequestMap.set(requestId, asyncRequest);
        return promise;
    };

    const completeRequest = (data) => {
        const {requestId, status, error, isHttpMessage} = data || {};
        const asyncRequest = asyncRequestMap.get(requestId);

        if (!asyncRequest) {
            log.debug(`${prefix} NOT FOUND REQUEST: RequestId ${requestId}, Status: ${status}`);
            return;
        }

        switch (status) {
            case successStatus:
                asyncRequest.clear();
                asyncRequest.resolve(data);
                log.debug(`${prefix} COMPLETED REQUEST: RequestId ${requestId}`);
                removeRequest(requestId);
                break;
            case failedStatus: {
                const parsedError = valueUtils.safeJsonParse(error);
                const errorCode = parsedError?.errorCode ?? error;

                asyncRequest.clear();
                asyncRequest.reject(
                    new AsyncRequestError({
                        message: `Request [${requestId}] failed`,
                        status,
                        code: errorCode,
                        requestId,
                    })
                );
                log.debug(`${prefix} FAILED REQUEST: RequestId ${requestId}`);
                removeRequest(requestId);
                break;
            }
            case pendingStatus:
                // update request time only for WebSocket messages
                if (!isHttpMessage) {
                    asyncRequest.updateLastResponseTime();
                }
                log.debug(`${prefix} PENDING REQUEST: RequestId ${requestId}`);
                break;
            default:
                log.warn(`${prefix} UNKNOWN STATUS: RequestId ${requestId}, Status: ${status}`, null);
                break;
        }
    };

    const cancelRequest = (asyncRequest) => {
        asyncRequest.clear();
        asyncRequest.reject(
            new AsyncRequestError({
                message: `Request [${asyncRequest.requestId}] cancelled due to WebSocket disconnection`,
                requestId: asyncRequest.requestId,
            })
        );
        log.debug(`${prefix} CANCELED REQUEST: RequestId ${asyncRequest.requestId}`, asyncRequest.dusn);
        removeRequest(asyncRequest.requestId);
    };

    const clearAsyncRequests = (dusn) => {
        asyncRequestMap.forEach((asyncRequest) => {
            if (dusn && asyncRequest.dusn === dusn) {
                cancelRequest(asyncRequest);
            }
        });
    };

    return {
        waitAsyncRequestComplete,
        completeRequest,
        clearAsyncRequests,
    };
};

export default createAsyncRequestService;
